import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Stack,
  Chip,
  AppBar,
  Toolbar,
  Avatar,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Description as DescriptionIcon,
  Engineering as EngineeringIcon,
  Verified as VerifiedIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  ArrowBack as ArrowBackIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { keyframes } from '@mui/system';

// Premium color palette for federal/enterprise sales
const colors = {
  // Primary gradient - deep blue to teal (trust, professionalism)
  primaryDark: '#0D1B2A',
  primaryMid: '#1B263B',
  primaryLight: '#415A77',
  
  // Accent - warm coral/orange (action, urgency)
  accent: '#FF6B35',
  accentLight: '#FF8C5A',
  accentGlow: 'rgba(255, 107, 53, 0.3)',
  
  // Secondary - teal/cyan (innovation, technology)
  teal: '#00B4D8',
  tealLight: '#48CAE4',
  tealGlow: 'rgba(0, 180, 216, 0.2)',
  
  // Neutrals
  white: '#FFFFFF',
  offWhite: '#F8F9FA',
  lightGray: '#E9ECEF',
  midGray: '#778DA9',
  darkGray: '#415A77',
  
  // Success
  success: '#10B981',
};

// Subtle animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

/**
 * PrimesLandingPage - Federal Prime Contractor Partner Page
 * 
 * Premium design focused on trust, professionalism, and conversion
 */
const PrimesLandingPage: React.FC = () => {
  const navigate = useNavigate();

  const BOOKING_LINK = 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ0N2nccvrbmxTCwRMt_Xiw_SZ6KHGvGA6JCi39FmeLQS_ZFPizhD-LrfsQrzOxeOKCv1Uj_LY5X?gv=true';

  const handleBookCall = () => {
    window.open(BOOKING_LINK, '_blank', 'noopener,noreferrer');
  };

  const capabilities = [
    {
      icon: <EngineeringIcon sx={{ fontSize: 32 }} />,
      title: 'Research-Backed Development',
      description: 'Full-stack engineering with documented methodologies, version control, and complete audit trails',
      gradient: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.tealLight} 100%)`,
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: 32 }} />,
      title: 'Audit-Ready Documentation',
      description: 'Technical documentation, system design artifacts, and compliance reports ready for agency review',
      gradient: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 32 }} />,
      title: 'Rapid Execution',
      description: 'Agile delivery with weekly demos, transparent timelines, and predictable milestones',
      gradient: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 32 }} />,
      title: 'Security-First Architecture',
      description: 'Built-in security patterns, encrypted data handling, and compliance-ready infrastructure',
      gradient: `linear-gradient(135deg, ${colors.success} 0%, #34D399 100%)`,
    },
  ];

  const services = [
    { name: 'Custom Software Development', icon: <EngineeringIcon /> },
    { name: 'Cloud Infrastructure', icon: <SecurityIcon /> },
    { name: 'Data Analytics', icon: <TrendingUpIcon /> },
    { name: 'API Integration', icon: <SpeedIcon /> },
    { name: 'DevOps & CI/CD', icon: <ShieldIcon /> },
    { name: 'Technical Documentation', icon: <DescriptionIcon /> },
    { name: 'System Architecture', icon: <BusinessIcon /> },
    { name: 'Legacy Modernization', icon: <TrendingUpIcon /> },
  ];

  const stats = [
    { value: '2 Weeks', label: 'Average Onboarding' },
    { value: '100%', label: 'Documentation Coverage' },
    { value: 'Weekly', label: 'Progress Demos' },
    { value: 'Audit-Ready', label: 'Deliverables' },
  ];

  const engagementModels = [
    {
      name: 'Retainer Partnership',
      description: 'Dedicated engineering capacity with flexible allocation across your portfolio',
      features: [
        'Dedicated team hours each month',
        'Priority scheduling for urgent needs',
        'Transparent time tracking & reporting',
        'Monthly strategic planning calls',
      ],
      recommended: true,
    },
    {
      name: 'Project-Based Delivery',
      description: 'Scoped work packages with fixed deliverables and timelines',
      features: [
        'Defined scope and acceptance criteria',
        'Milestone-based payments',
        'Complete documentation package',
        'Knowledge transfer sessions',
      ],
      recommended: false,
    },
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Fit Call',
      description: '15-minute call to understand your delivery needs and requirements',
    },
    {
      step: '02',
      title: 'Scope & SOW',
      description: 'Define clear deliverables, timelines, and documentation needs',
    },
    {
      step: '03',
      title: 'Execute & Report',
      description: 'Weekly progress demos with transparent communication',
    },
    {
      step: '04',
      title: 'Deliver & Support',
      description: 'Knowledge transfer, documentation, and maintenance options',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${colors.primaryDark} 0%, ${colors.primaryMid} 50%, ${colors.primaryDark} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '50%',
          height: '50%',
          background: `radial-gradient(circle, ${colors.tealGlow} 0%, transparent 70%)`,
          filter: 'blur(100px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          left: '-10%',
          width: '40%',
          height: '40%',
          background: `radial-gradient(circle, ${colors.accentGlow} 0%, transparent 70%)`,
          filter: 'blur(100px)',
          pointerEvents: 'none',
        }}
      />

      {/* Navigation Header */}
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          background: `rgba(13, 27, 42, 0.9)`,
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid rgba(255, 255, 255, 0.08)`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Typography
                component="a"
                href="/landing.html"
                sx={{
                  fontWeight: 900,
                  fontSize: '1.5rem',
                  background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.accent} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textDecoration: 'none',
                  letterSpacing: '-0.02em',
                }}
              >
                REDEX
              </Typography>
              <Chip
                label="PARTNER PROGRAM"
                size="small"
                sx={{
                  background: 'rgba(0, 180, 216, 0.15)',
                  color: colors.tealLight,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  letterSpacing: '0.05em',
                  border: `1px solid rgba(0, 180, 216, 0.3)`,
                }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                component="a"
                href="/landing.html"
                startIcon={<ArrowBackIcon />}
                sx={{ 
                  color: colors.midGray, 
                  textTransform: 'none',
                  '&:hover': { color: colors.white },
                }}
              >
                Home
              </Button>
              <Button
                variant="contained"
                onClick={handleBookCall}
                sx={{ 
                  background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  boxShadow: `0 4px 20px ${colors.accentGlow}`,
                  '&:hover': {
                    boxShadow: `0 6px 30px ${colors.accentGlow}`,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Schedule Call
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: 16, pb: 10 }}>
        {/* Hero Section */}
        <Box 
          sx={{ 
            textAlign: 'center', 
            mb: 10,
            animation: `${fadeInUp} 0.8s ease-out`,
          }}
        >
          <Chip
            icon={<BusinessIcon sx={{ fontSize: 16 }} />}
            label="FOR FEDERAL PRIME CONTRACTORS"
            sx={{
              mb: 4,
              py: 2.5,
              px: 1,
              background: 'rgba(255, 107, 53, 0.1)',
              border: `1px solid rgba(255, 107, 53, 0.3)`,
              color: colors.accentLight,
              fontWeight: 700,
              fontSize: '0.8rem',
              letterSpacing: '0.08em',
              '& .MuiChip-icon': { color: colors.accent },
            }}
          />
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 800,
              color: colors.white,
              mb: 3,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
          >
            Meet Your Subcontracting Goals
            <br />
            <Box 
              component="span" 
              sx={{ 
                background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.tealLight} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              With Qualified Engineering Support
            </Box>
          </Typography>
          <Typography
            sx={{
              color: colors.midGray,
              mb: 5,
              maxWidth: '700px',
              mx: 'auto',
              fontSize: '1.2rem',
              lineHeight: 1.7,
            }}
          >
            REDEX Labs helps federal prime contractors deliver technical work 
            with research-backed development, audit-ready documentation, 
            and fast execution — reducing delivery risk while supporting your 
            small business subcontracting objectives.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleBookCall}
              startIcon={<PhoneIcon />}
              sx={{
                py: 2,
                px: 5,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
                boxShadow: `0 8px 30px ${colors.accentGlow}`,
                borderRadius: 2,
                '&:hover': {
                  boxShadow: `0 12px 40px rgba(255, 107, 53, 0.5)`,
                  transform: 'translateY(-3px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Book a 15-Min Fit Call
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/partners/why-redex')}
              sx={{
                py: 2,
                px: 5,
                fontSize: '1.1rem',
                fontWeight: 600,
                color: colors.white,
                borderColor: 'rgba(255, 255, 255, 0.3)',
                borderRadius: 2,
                '&:hover': {
                  borderColor: colors.teal,
                  background: 'rgba(0, 180, 216, 0.1)',
                },
              }}
            >
              Why Work With Us
            </Button>
          </Stack>
        </Box>

        {/* Stats Bar */}
        <Paper
          elevation={0}
          sx={{
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            p: 4,
            mb: 10,
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: '2rem',
                      fontWeight: 800,
                      background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.tealLight} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.5,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography sx={{ color: colors.midGray, fontSize: '0.9rem' }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Capabilities Section */}
        <Box sx={{ mb: 12 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                color: colors.white,
                mb: 2,
              }}
            >
              How We Help Primes Deliver
            </Typography>
            <Typography
              sx={{
                color: colors.midGray,
                maxWidth: '600px',
                mx: 'auto',
                fontSize: '1.1rem',
              }}
            >
              Technical execution that meets federal standards while 
              reducing your delivery risk and headcount burden
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {capabilities.map((capability, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.02)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    transition: 'all 0.4s ease',
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      '& .capability-icon': {
                        transform: 'scale(1.1)',
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: capability.gradient,
                    }}
                  />
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      className="capability-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        background: capability.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        color: colors.white,
                        transition: 'transform 0.3s ease',
                      }}
                    >
                      {capability.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ color: colors.white, mb: 2, fontWeight: 600 }}
                    >
                      {capability.title}
                    </Typography>
                    <Typography sx={{ color: colors.midGray, lineHeight: 1.7 }}>
                      {capability.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Services Grid */}
        <Box sx={{ mb: 12 }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: colors.white,
              mb: 5,
            }}
          >
            Technical Services
          </Typography>
          <Grid container spacing={2}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    cursor: 'default',
                    '&:hover': {
                      background: 'rgba(0, 180, 216, 0.08)',
                      border: `1px solid ${colors.teal}`,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ color: colors.teal }}>{service.icon}</Box>
                    <Typography sx={{ color: colors.white, fontWeight: 500 }}>
                      {service.name}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Process Steps */}
        <Box sx={{ mb: 12 }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: colors.white,
              mb: 6,
            }}
          >
            How We Work Together
          </Typography>
          <Grid container spacing={4}>
            {processSteps.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box 
                  sx={{ 
                    textAlign: 'center',
                    position: 'relative',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '4rem',
                      fontWeight: 900,
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      lineHeight: 1,
                      mb: 2,
                    }}
                  >
                    {step.step}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ 
                      color: colors.white, 
                      mb: 1.5, 
                      fontWeight: 600,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography 
                    sx={{ 
                      color: colors.midGray, 
                      lineHeight: 1.6,
                      fontSize: '0.95rem',
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Engagement Models */}
        <Box sx={{ mb: 12 }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: colors.white,
              mb: 2,
            }}
          >
            Engagement Models
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: colors.midGray,
              mb: 6,
              maxWidth: '500px',
              mx: 'auto',
            }}
          >
            Flexible partnership structures to match your contract requirements
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {engagementModels.map((model, index) => (
              <Grid item xs={12} md={5} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    background: model.recommended 
                      ? 'linear-gradient(135deg, rgba(0, 180, 216, 0.1) 0%, rgba(255, 107, 53, 0.05) 100%)'
                      : 'rgba(255, 255, 255, 0.02)',
                    borderRadius: 3,
                    border: model.recommended 
                      ? `2px solid ${colors.teal}`
                      : '1px solid rgba(255, 255, 255, 0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {model.recommended && (
                    <Chip
                      label="RECOMMENDED"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        background: colors.teal,
                        color: colors.white,
                        fontWeight: 700,
                        fontSize: '0.65rem',
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{ 
                        color: model.recommended ? colors.tealLight : colors.white, 
                        mb: 1, 
                        fontWeight: 700,
                      }}
                    >
                      {model.name}
                    </Typography>
                    <Typography sx={{ color: colors.midGray, mb: 3 }}>
                      {model.description}
                    </Typography>
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', mb: 3 }} />
                    <List dense disablePadding>
                      {model.features.map((feature, i) => (
                        <ListItem key={i} sx={{ px: 0, py: 0.75 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon sx={{ fontSize: 18, color: colors.success }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ 
                              color: colors.lightGray,
                              fontSize: '0.95rem',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Final CTA Section */}
        <Paper
          sx={{
            background: `linear-gradient(135deg, ${colors.accent} 0%, #FF8C5A 50%, ${colors.teal} 100%)`,
            backgroundSize: '200% 200%',
            animation: `${shimmer} 8s ease infinite`,
            borderRadius: 4,
            p: { xs: 5, md: 8 },
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.2)',
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{ 
                color: colors.white, 
                mb: 2, 
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
              }}
            >
              Ready to Discuss Your Subcontracting Needs?
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                mb: 5,
                maxWidth: '600px',
                mx: 'auto',
                fontSize: '1.1rem',
              }}
            >
              Schedule a 15-minute call to explore how REDEX Labs can help you 
              deliver technical work while meeting your small business goals.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleBookCall}
              startIcon={<ScheduleIcon />}
              sx={{
                py: 2,
                px: 6,
                fontSize: '1.15rem',
                fontWeight: 700,
                backgroundColor: colors.white,
                color: colors.primaryDark,
                borderRadius: 2,
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  backgroundColor: colors.offWhite,
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Book a Free Consultation
            </Button>
          </Box>
        </Paper>

        {/* Disclaimer */}
        <Typography
          sx={{
            textAlign: 'center',
            color: colors.darkGray,
            mt: 8,
            fontSize: '0.75rem',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          <strong>Disclaimer:</strong> Information on this page is for general 
          informational purposes only and does not constitute legal, tax, or 
          regulatory advice. Federal contracting requirements vary by agency 
          and contract. Please consult with your legal and compliance advisors 
          regarding your specific subcontracting obligations and requirements.
        </Typography>
      </Container>
    </Box>
  );
};

export default PrimesLandingPage;
